/**
 * projetos-store — A4.3.3 / Master Plan A4.
 *
 * React Query hooks pro módulo de Projetos.
 * Tipos mirror dos repos backend (server/lib/repositories/projetos.repo.ts).
 */
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api, hasToken } from './api';

// ─── Tipos ─────────────────────────────────────────────────────────────

// Projetos 2.0 F1.1 — tipo da origem do projeto.
export type OrigemWorkflow = 'avulso' | 'comercial' | 'manutencao';
export type EtapaStatus = 'pendente' | 'em_andamento' | 'concluido' | 'bloqueado';
export type TarefaPrioridade = 'baixa' | 'media' | 'alta' | 'critica';
export type CustoCategoria =
  | 'material'
  | 'mao_obra_terceira'
  | 'mao_obra_propria'
  | 'logistica'
  | 'outros';

export const CUSTO_CATEGORIA_LABEL: Record<CustoCategoria, string> = {
  material: 'Material',
  mao_obra_terceira: 'Mão de obra terceira',
  mao_obra_propria: 'Mão de obra própria',
  logistica: 'Logística',
  outros: 'Outros',
};

export const ETAPA_STATUS_LABEL: Record<EtapaStatus, string> = {
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  concluido: 'Concluído',
  bloqueado: 'Bloqueado',
};

export interface Projeto {
  id: string;
  empresaId: string;
  clienteId: string;
  tipoId: string;
  origemOrcamentoId: string | null;
  origemWorkflow: OrigemWorkflow | null;
  /** PRD 1.4 (mig 603): a proposta do funil que gerou o projeto; a revisão aceita; o vendido congelado. */
  cardId: string | null;
  orcamentoId: string | null;
  valorVendidoCentavos: number | null;
  nome: string;
  descricao: string | null;
  status: string | null;
  // B1: número humano do projeto (sequência própria, separada do card).
  numeroHumano: number | null;
  /** PROJETOS-LIMPO 2: a situação de prazo da linha, pelo motor da ficha (só na lista). */
  prazo?: PrazoNaLista | null;
  /** CRONOGRAMA-MSP item 11 (só na lista): quando o arquivo entrou, e se a obra em andamento está parada além do limite. */
  cronogramaAtualizado?: (UltimaImportacao & { dias: number | null; parado: boolean; limite: number }) | null;
  valorVendaTotal: number | null;
  dataInicioPrevisto: string | null;
  dataFimPrevisto: string | null;
  dataInicioReal: string | null;
  dataFimReal: string | null;
  responsavelId: string | null;
  criadoPor: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  clienteRazaoSocial?: string;
  tipoNome?: string;
  responsavelNome?: string;
  /** 🔴 #378 (mig 647): o fim da obra — quem concluiu/cancelou e quando. */
  concluidoEmIso?: string | null;
  concluidoPorNome?: string | null;
  canceladoEmIso?: string | null;
  canceladoPorNome?: string | null;
  canceladoMotivo?: string | null;
  /** 🔴 #378: o que ESTA pessoa pode fazer com o fim da obra (só no GET /:id; o servidor decide). */
  encerramento?: EncerramentoDaObra;
}

export interface EncerramentoDaObra {
  podeReabrir: boolean;
  podeApagar: boolean;
  /** solicitações do Compras abertas ou classificadas: com alguma, cancelar e apagar são recusados */
  solicitacoesVivas: number;
  /** a frase da recusa do cancelar (a mesma do 409) — null sem solicitação viva */
  fraseDoComprasVivo: string | null;
  /** a pergunta do concluir com Compras vivo ("Concluir assim mesmo?") — avisa, não recusa; null sem solicitação viva */
  fraseDoComprasVivoNoConcluir: string | null;
  /** a frase da obra cancelada (a mesma que o servidor devolve no 409) — null quando não está cancelada */
  fraseDoCancelado: string | null;
}

export interface ProjetoTipo {
  id: string;
  empresaId: string;
  nome: string;
  descricao: string | null;
  workflowJson: string | null;
  ativo: boolean;
  /** M1: tipo pai (null = tipo de topo). Hierarquia de 2 níveis (Tipo → Subtipo). */
  parentTipoId: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  workflowSteps?: WorkflowStep[];
}

export interface WorkflowStep {
  id: string;
  projetoTipoId: string;
  ordem: number;
  nome: string;
  descricao: string | null;
  requerAprovacao: boolean;
  perfilAprovador: string | null;
  criadoEmIso: string;
}

/**
 * Achata os tipos numa ordem "tipo de topo seguido dos seus subtipos", com o
 * subtipo recuado (`↳ `). Fonte única reusada pelos seletores de tipo (Novo
 * orçamento, Modelos de Orçamento) — antes a mesma lógica vivia duplicada em
 * cada tela (M1 / AO-5).
 */
export function flattenTiposComSubtipos(
  tipos: ProjetoTipo[],
): Array<{ id: string; label: string }> {
  const topo = tipos.filter((t) => t.parentTipoId === null);
  const out: Array<{ id: string; label: string }> = [];
  for (const t of topo) {
    out.push({ id: t.id, label: t.nome });
    for (const sub of tipos.filter((s) => s.parentTipoId === t.id)) {
      out.push({ id: sub.id, label: `↳ ${sub.nome}` });
    }
  }
  return out;
}

// B2: modelo (template) de etapa por tipo de projeto. Ao "Iniciar", cada modelo
// vira uma etapa real do cronograma do projeto.
export interface EtapaModelo {
  id: string;
  tipoId: string;
  ordem: number;
  nome: string;
  descricao: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface Etapa {
  id: string;
  projetoId: string;
  // F1.2: árvore. NULL = etapa de topo.
  parentEtapaId: string | null;
  ordem: number;
  nome: string;
  descricao: string | null;
  status: EtapaStatus;
  responsavelId: string | null;
  // P2.4 (cronograma duplo): `dataInicio`/`dataFim` = Prazo EQUIPE (interno);
  // `dataInicioCliente`/`dataFimCliente` = Prazo CLIENTE (divulgado, com folga).
  dataInicio: string | null;
  dataFim: string | null;
  dataInicioCliente: string | null;
  dataFimCliente: string | null;
  // F1.2: datas efetivas (rollup) — folha = digitada; pai = min/max dos filhos.
  // Um par por prazo (equipe + cliente).
  dataInicioEfetiva: string | null;
  dataFimEfetiva: string | null;
  dataInicioClienteEfetiva: string | null;
  dataFimClienteEfetiva: string | null;
  // F1.3: % de andamento. concluido = valor manual da folha; override = pino
  // manual (vence o cálculo); efetivo = valor calculado (read-only).
  percentualConcluido: number;
  percentualOverride: number | null;
  percentualEfetivo: number;
  /** mig 613: o UID da tarefa no MS Project. Com ele, a etapa VEIO DO ARQUIVO — o % é só leitura e a situação deriva do %. */
  mspUid?: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  responsavelNome?: string;
  tarefas?: Tarefa[];
}

/** Rollup do projeto (datas + % derivados das etapas de topo). */
export interface ProjetoRollup {
  dataInicio: string | null;
  dataFim: string | null;
  // P2.4: rollup do prazo cliente.
  dataInicioCliente: string | null;
  dataFimCliente: string | null;
  percentual: number;
}

export interface Tarefa {
  id: string;
  etapaId: string;
  titulo: string;
  descricao: string | null;
  responsavelId: string | null;
  status: EtapaStatus;
  prioridade: TarefaPrioridade;
  dataPrevista: string | null;
  concluidoEmIso: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  responsavelNome?: string;
}

// PROJETOS-LIMPO 2 (14/09/2026): o lançamento manual de custo MORREU (decisão do Matheus) —
// `Custo`, `DesvioReport`, `useCustos`, `useDesvio`, `useCreateCusto`, `useDeleteCusto` e
// `useProjetoCustoResumo` saíram. Os números de custo vivem no motor da obra (`useProjetoIndicadores`).

export interface Anexo {
  id: string;
  projetoId: string;
  etapaId: string | null;
  tarefaId: string | null;
  r2Key: string;
  urlR2: string;
  nomeOriginal: string;
  mimetype: string;
  tamanhoBytes: number;
  uploadedBy: string | null;
  uploadedEmIso: string;
  uploadedByNome?: string;
}

export interface CreateProjetoInput {
  clienteId: string;
  tipoId: string;
  nome: string;
  descricao?: string | null;
  origemOrcamentoId?: string | null;
  origemWorkflow?: OrigemWorkflow | null;
  valorVendaTotal?: number | null;
  responsavelId?: string | null;
}

export interface UpdateProjetoInput {
  tipoId?: string;
  nome?: string;
  descricao?: string | null;
  status?: string | null;
  valorVendaTotal?: number | null;
  dataInicioReal?: string | null;
  dataFimReal?: string | null;
  responsavelId?: string | null;
}

export interface ListProjetosFilters {
  clienteId?: string;
  tipoId?: string;
  status?: string;
  responsavelId?: string;
  search?: string;
}

// Projetos 2.0 F2.2 — solicitação do projeto que virou card (Compras/Financeiro).
export type SolicitacaoWorkflow = 'compras' | 'financeiro';

export interface SolicitacaoProjeto {
  relationId: string;
  cardId: string;
  cardType: 'deal' | 'service_order';
  workflow: string;
  fase: string;
  descricao: string;
  valor: number;
  numeroHumano: number | null;
  responsavelId: string | null;
  responsavelNome: string | null;
  etapaId: string | null;
  etapaNome: string | null;
  criadoEmIso: string;
}

// ─── Hooks ─────────────────────────────────────────────────────────────

const QK = {
  list: (f: ListProjetosFilters) => ['projetos', 'list', f] as const,
  detail: (id: string) => ['projetos', 'detail', id] as const,
  etapas: (id: string) => ['projetos', id, 'etapas'] as const,
  // Round C — lista de itens em camadas (venda/levantamento)
  itens: (id: string) => ['projetos', id, 'itens'] as const,
  // PROJETOS-LIMPO 2: o motor da obra (a Visão geral e a aba Indicadores leem esta chave)
  indicadores: (id: string) => ['projetos', id, 'indicadores'] as const,
  solicitacoes: (id: string) => ['projetos', id, 'solicitacoes'] as const,
  comprasDaObra: (id: string) => ['projetos', id, 'compras-da-obra'] as const,
  tipos: () => ['projeto-tipos', 'list'] as const,
  tipoDetail: (id: string) => ['projeto-tipos', 'detail', id] as const,
  // P1 (Parte 2)
  pendentes: () => ['projetos', 'pendentes'] as const,
  slaIniciar: () => ['projetos', 'config', 'sla-iniciar'] as const,
};

function buildQuery(filters: ListProjetosFilters): string {
  const params: string[] = [];
  if (filters.clienteId) params.push(`clienteId=${encodeURIComponent(filters.clienteId)}`);
  if (filters.tipoId) params.push(`tipoId=${encodeURIComponent(filters.tipoId)}`);
  if (filters.status) params.push(`status=${encodeURIComponent(filters.status)}`);
  if (filters.responsavelId)
    params.push(`responsavelId=${encodeURIComponent(filters.responsavelId)}`);
  if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
  return params.length ? `?${params.join('&')}` : '';
}

export function useProjetos(filters: ListProjetosFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: () => api.get<{ items: Projeto[]; total: number }>(`/api/v1/projetos${buildQuery(filters)}`),
    enabled: hasToken(),
  });
}

export function useProjeto(id: string | undefined) {
  return useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => api.get<Projeto>(`/api/v1/projetos/${id}`),
    enabled: hasToken() && !!id,
  });
}

// ─── P1 (Parte 2): quadro de pendentes + SLA "prazo pra iniciar" ────────

/**
 * Projeto pendente (ainda não iniciado) com o indicador de SLA já calculado
 * pelo backend. `slaVencido` = passou do prazo pra iniciar (pisca na lista);
 * `diasRestantes` negativo = atrasado.
 */
export interface ProjetoPendente extends Projeto {
  slaDias: number;
  diasRestantes: number;
  slaVencido: boolean;
}

/**
 * Lista de projetos pendentes (origem automática de venda que ainda não foi
 * iniciada). Poll de 60s pra refletir o avanço do SLA e novas vendas ganhas
 * sem reload (consistente com o pattern backend-first das outras stores).
 */
export function useProjetosPendentes() {
  return useQuery({
    queryKey: QK.pendentes(),
    queryFn: () =>
      api.get<{ items: ProjetoPendente[]; total: number; slaDias: number }>(
        '/api/v1/projetos/pendentes',
      ),
    enabled: hasToken(),
    refetchInterval: 60_000,
  });
}

/** CRONOGRAMA-MSP item 11: o limite (dias) do selo "cronograma sem atualizar" na lista. Nasce 7. */
export function useDiasSemAtualizar() {
  return useQuery({
    queryKey: ['projetos', 'config', 'cronograma-sem-atualizar'] as const,
    queryFn: () => api.get<{ dias: number }>('/api/v1/projetos/config/cronograma-sem-atualizar'),
    enabled: hasToken(),
  });
}
export function useSetDiasSemAtualizar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dias: number) => api.put<{ dias: number }>('/api/v1/projetos/config/cronograma-sem-atualizar', { dias }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projetos', 'config', 'cronograma-sem-atualizar'] });
      qc.invalidateQueries({ queryKey: ['projetos', 'list'] });
    },
  });
}

/** SLA "prazo pra iniciar" (dias). Default 5 no backend. */
export function useSlaIniciar() {
  return useQuery({
    queryKey: QK.slaIniciar(),
    queryFn: () => api.get<{ dias: number }>('/api/v1/projetos/config/sla-iniciar'),
    enabled: hasToken(),
  });
}

/** Salva o SLA "prazo pra iniciar" (PUT — só perfil administrativo no backend). */
export function useSetSlaIniciar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dias: number) =>
      api.put<{ dias: number }>('/api/v1/projetos/config/sla-iniciar', { dias }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.slaIniciar() });
      qc.invalidateQueries({ queryKey: QK.pendentes() });
    },
  });
}

// ─── P3 (Parte 2): iniciar projeto ──────────────────────────────────────
// PROJETOS-LIMPO 2 (14/09/2026): o catálogo de Atividades padrão e as tarefas geradas ao
// iniciar MORRERAM (decisão do Matheus: "o que não tem uso hoje não nasce" — seis tarefas
// intocadas desde 01/07; o Cronograma faz isso melhor). Iniciar só marca em andamento.

/**
 * Inicia um projeto pendente → marca em andamento (o cronograma nasce no levantamento).
 * B2: `tipoId` é exigido quando o projeto está "A definir".
 */
export function useIniciarProjeto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projetoId, tipoId }: { projetoId: string; tipoId?: string }) =>
      api.post<{ ok: boolean; projeto: Projeto }>(
        `/api/v1/projetos/${projetoId}/iniciar`,
        tipoId ? { tipoId } : {},
      ),
    onSuccess: (_data, { projetoId }) => {
      qc.invalidateQueries({ queryKey: QK.pendentes() });
      qc.invalidateQueries({ queryKey: QK.detail(projetoId) });
      qc.invalidateQueries({ queryKey: ['projetos', 'list'] });
    },
  });
}

// ─── P4 (Parte 2): documentos exigidos da venda ─────────────────────────

/** Tipo de documento exigido (catálogo global). */
// A2: 'projeto' | 'recorrente' | null (null = exigido em qualquer venda).
export type DocTipoVenda = 'projeto' | 'recorrente';

export interface DocumentoTipo {
  id: string;
  nome: string;
  ordem: number;
  ativo: boolean;
  // A2: a qual tipo de venda o documento se aplica. null = todos (global).
  tipoVenda: DocTipoVenda | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

// SU-3 (situação unificada): o catálogo P7 "Situação do material" foi removido —
// o status canônico (solicitacao_compra_item.situacao) substitui. Hooks/tipo/rota
// e a página admin saíram juntos (mig 289).

export function useDocumentoTipos(apenasAtivos = false) {
  return useQuery({
    queryKey: ['projeto-documento-tipos', 'list', apenasAtivos] as const,
    queryFn: () =>
      api.get<{ items: DocumentoTipo[] }>(
        `/api/v1/projeto-documento-tipos${apenasAtivos ? '?ativos=1' : ''}`,
      ),
    enabled: hasToken(),
  });
}

export function useDocumentoTipoActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['projeto-documento-tipos'] });
  const createTipo = useMutation({
    mutationFn: (input: { nome: string; ativo?: boolean; tipoVenda?: DocTipoVenda | null }) =>
      api.post<DocumentoTipo>('/api/v1/projeto-documento-tipos', input),
    onSuccess: invalidate,
  });
  const updateTipo = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { nome?: string; ordem?: number; ativo?: boolean; tipoVenda?: DocTipoVenda | null };
    }) => api.patch<DocumentoTipo>(`/api/v1/projeto-documento-tipos/${id}`, patch),
    onSuccess: invalidate,
  });
  const deleteTipo = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/projeto-documento-tipos/${id}`),
    onSuccess: invalidate,
  });
  const reorderTipos = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put<{ items: DocumentoTipo[] }>('/api/v1/projeto-documento-tipos/reorder', { orderedIds }),
    onSuccess: invalidate,
  });
  return { createTipo, updateTipo, deleteTipo, reorderTipos };
}

/** Documento anexado à venda (deal). */
export interface DealDocumento {
  id: string;
  dealId: string;
  tipoId: string | null;
  nomeArquivo: string;
  r2Key: string;
  mimetype: string | null;
  tamanhoBytes: number | null;
  criadoEmIso: string;
  tipoNome?: string | null;
}

export interface DealDocumentosResponse {
  items: DealDocumento[];
  tiposExigidos: DocumentoTipo[];
  faltando: Array<{ id: string; nome: string }>;
}

export function useDealDocumentos(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['deal-documentos', dealId ?? ''] as const,
    queryFn: () => api.get<DealDocumentosResponse>(`/api/v1/deals/${dealId}/documentos`),
    enabled: hasToken() && !!dealId,
  });
}

export function useDealDocumentoActions(dealId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['deal-documentos', dealId] });
  const requestUploadUrl = useMutation({
    mutationFn: (input: { nomeOriginal: string; mimetype: string; tamanhoBytes: number }) =>
      api.post<{ uploadUrl: string; r2Key: string; expiresAt: string }>(
        `/api/v1/deals/${dealId}/documentos/upload-url`,
        input,
      ),
  });
  const registerDoc = useMutation({
    mutationFn: (input: {
      tipoId?: string | null;
      nomeArquivo: string;
      r2Key: string;
      urlR2?: string | null;
      mimetype?: string | null;
      tamanhoBytes?: number | null;
    }) => api.post<DealDocumento>(`/api/v1/deals/${dealId}/documentos`, input),
    onSuccess: invalidate,
  });
  const deleteDoc = useMutation({
    mutationFn: (docId: string) => api.delete<void>(`/api/v1/deals/documentos/${docId}`),
    onSuccess: invalidate,
  });
  return { requestUploadUrl, registerDoc, deleteDoc };
}

/** URL presigned pra visualizar um documento da venda. */
export function useDealDocumentoUrl() {
  return useMutation({
    mutationFn: (docId: string) =>
      api.get<{ url: string; expiresAt: string }>(`/api/v1/deals/documentos/${docId}/url`),
  });
}

/** Documentos da venda herdados por um projeto (read-only). */
export function useProjetoDocumentosVenda(projetoId: string | undefined) {
  return useQuery({
    queryKey: ['projetos', projetoId ?? '', 'documentos-venda'] as const,
    queryFn: () =>
      api.get<{ items: DealDocumento[]; origemDealId: string | null }>(
        `/api/v1/projetos/${projetoId}/documentos-venda`,
      ),
    enabled: hasToken() && !!projetoId,
  });
}

// ─── P8: indicadores do projeto ──────────────────────────────────────

/**
 * PRD 1.5 — o PRAZO lê o cronograma (motor único no servidor, `prazoDoProjeto`).
 * Início = primeira etapa com data; prometida = início + mo_dias do orçamento em
 * dias úteis; fim real = o registrado. ❌ Nenhum campo manual.
 */
export type SituacaoPrazo =
  | 'sem-cronograma'
  | 'sem-mo-dias'
  | 'no-prazo'
  | 'atrasado'
  | 'concluido-no-prazo'
  | 'concluido-atrasado'
  | 'concluido-sem-medida'
  /** 🔴 #378: obra cancelada — o prazo não se mede mais */
  | 'cancelado';

export interface PrazoDoProjeto {
  dataInicio: string | null;
  moDias: number | null;
  temOrcamento: boolean;
  dataPrometida: string | null;
  dataFimEquipe: string | null;
  dataFimReal: string | null;
  concluido: boolean;
  /** dias úteis: positivo faltam/sobraram, negativo de atraso; null sem prometida */
  diasUteis: number | null;
  situacao: SituacaoPrazo;
  rotulo: string;
  frase: string;
}

export type PrazoNaLista = Pick<PrazoDoProjeto, 'situacao' | 'rotulo' | 'dataInicio' | 'dataPrometida' | 'diasUteis'>;

/**
 * 🔴 O MOTOR DA OBRA (PROJETOS-LIMPO 2, 14/09/2026) — `GET /projetos/:id/indicadores`.
 * A Visão geral (os cinco números) e a aba Indicadores (o resto) leem ESTE objeto.
 * Nenhuma aba soma, subtrai ou divide: o motor entrega pronto, a tela formata.
 */
/** A-OBRA-PRECISA-DIZER-COMO-VAI (15/09): a régua de cor do Financeiro, agora na obra. */
export type NivelSaude = 'verde' | 'amarelo' | 'vermelho' | 'neutro';
export type RotuloSemaforo = 'no esperado' | 'atenção' | 'fora do esperado';
export interface SemaforoDoBloco {
  nivel: 'verde' | 'amarelo' | 'vermelho';
  rotulo: RotuloSemaforo;
  /** a régua aplicada, em português */
  motivo: string;
}
/** O que um bloco diz de si: semáforo (ou nenhum) e, SEM DADO, a frase no lugar do número. */
export interface EstadoDoBloco {
  semaforo: SemaforoDoBloco | null;
  semDado: boolean;
  frase: string | null;
  /** 🔴 #365: o número está incompleto, e a frase diz por quê (custo e margem). */
  ressalva?: string | null;
}

/** #313: a proposta de hoje × o que o Ganho congelou. O motor compara; a tela só formata. */
export interface DivergenciaDaVenda {
  divergiu: boolean;
  vendidoCongeladoCentavos: number;
  propostaHojeCentavos: number;
  moDiasCongelados: number;
  moDiasHoje: number;
  itens: { novos: number; removidos: number; alterados: number };
  revisaoMudou: boolean;
  revisaoHojeId: string;
  ultimaEdicao: { quandoIso: string; quemNome: string | null; registrada: boolean } | null;
}

export interface ProjetoIndicadores {
  prazo: PrazoDoProjeto & {
    /** = dataInicio (o início do cronograma) */
    dataInicioPrevisto: string | null;
    /** = dataPrometida */
    dataFimPrevisto: string | null;
    dataInicioReal: string | null;
    dataFimReal: string | null;
    /** = diasUteis */
    diasRestantes: number | null;
    atrasado: boolean;
  };
  andamento: {
    percentual: number;
    etapas: { total: number; concluidas: number; emAndamento: number };
  };
  /** #308/#309 — um Vendido só: a venda congelada no Ganho. */
  vendido: { centavos: number | null; fonte: 'orcamento-aprovado' | 'itens' | 'sem-venda' };
  custo: {
    previstoCentavos: number;
    realizadoCentavos: number;
    imprevistoCentavos: number;
    desvioCentavos: number;
    desvioPct: number | null;
    lancamentos: number;
    porCategoria: Array<{ categoria: CustoCategoria; realizadoCentavos: number }>;
    /** 🔴 #365: compras que aconteceram sem custo lançado. */
    comprasSemCusto?: ComprasSemCusto;
  };
  margem: {
    previstaCentavos: number | null;
    previstaPct: number | null;
    realCentavos: number | null;
    realPct: number | null;
    imprevistoPct: number | null;
  };
  podeVerFinanceiro: boolean;
  /**
   * Decisão do Matheus (13/09): comissões só para o departamento Financeiro e admin.
   * É a chave `financeiro.ver` do motor de acesso, decidida na tela de Permissões.
   */
  podeVerComissoes: boolean;
  /** A faixa do topo: o veredito da obra em palavras, com a cor do estado. Vem do motor. */
  saude: { nivel: NivelSaude; frase: string };
  /** O que cada bloco da Visão geral diz de si (semáforo e "sem dado"). Vem do motor. */
  blocos: {
    vendido: EstadoDoBloco;
    prazo: EstadoDoBloco;
    andamento: EstadoDoBloco & { prazoConsumidoPct: number | null };
    custo: EstadoDoBloco;
    margem: EstadoDoBloco;
  };
  /**
   * Fase 2 de Compras (decisão do Matheus, 15/09/2026): o que a IGM ganha nos itens faturados DIRETO —
   * a margem líquida (margem − imposto do distribuidor). Entra na margem real e a tela DIZ
   * "R$ X vieram do faturamento direto, não do Omie". `margemLiquidaCentavos` null = incompleta:
   * `fornecedoresSemImposto` diz quem está sem o % no cadastro.
   */
  /** #313: null = obra sem venda com orçamento. */
  divergenciaDaVenda: DivergenciaDaVenda | null;
  /** #313: a régua do gesto, decidida no servidor (administrativo). */
  podeAtualizarVenda?: boolean;
  faturamentoDireto: {
    itens: number;
    margemCentavos: number;
    impostoDistribuidorCentavos: number | null;
    /** O que a IGM tem hoje (crédito retido no fornecedor) — é o que entra na margem da obra. */
    margemComCreditoRetidoCentavos: number | null;
    /** "Se retirar, sobram" — com a NF de serviço descontada de quem cobra. */
    margemSeRetirarCentavos: number | null;
    itensQueCobramNaRetirada: number;
    /** Algum item cobra na retirada e a empresa não tem alíquota configurada — o "se retirar" não sai. */
    retiradaSemAliquota: boolean;
    fornecedoresSemImposto: string[];
  };
  /** Indicadores: o custo realizado (e a margem real) mês a mês. */
  historico: Array<{ mes: string; realizadoCentavos: number; acumuladoCentavos: number; margemRealCentavos: number | null }>;
  /** Indicadores: venda × levantamento. */
  comparacoes: {
    vendaItens: number;
    levantamentoItens: number;
    custoVendaCentavos: number;
    custoLevantamentoCentavos: number;
    temLevantamento: boolean;
  };
}

/** PROJETOS-LIMPO 2: os cinco números do topo da lista (`GET /projetos/painel`). */
export interface PainelDeProjetos {
  esperandoComecar: number;
  emAndamento: number;
  noPrazo: number;
  atrasados: number;
  concluidos: number;
  total: number;
}

export function useProjetosPainel(enabled = true) {
  return useQuery({
    queryKey: ['projetos', 'painel'] as const,
    queryFn: () => api.get<PainelDeProjetos>('/api/v1/projetos/painel'),
    enabled: hasToken() && enabled,
    refetchInterval: 60_000,
  });
}

// ─── Documentos da obra — o LINK do Google Drive (mig 610) ──────────────────
// 🔴 O v2 não guarda arquivo (decisão do Matheus, 14/09): guarda o link e quem/quando.

export interface ProjetoDocumentoLink {
  id: string;
  projetoId: string;
  titulo: string;
  url: string;
  ligadoPor: string;
  ligadoPorNome: string | null;
  ligadoEmIso: string;
}

export interface ProjetoDocumentosResposta {
  links: ProjetoDocumentoLink[];
  /** O que o Comercial anexou no card da venda (até a rodada que o leva ao link). */
  anexadosNoComercial: DealDocumento[];
}

export function useProjetoDocumentos(projetoId: string | undefined) {
  return useQuery({
    queryKey: ['projetos', projetoId ?? '', 'documentos'] as const,
    queryFn: () => api.get<ProjetoDocumentosResposta>(`/api/v1/projetos/${projetoId}/documentos`),
    enabled: hasToken() && !!projetoId,
  });
}

export function useLigarDocumento(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { titulo: string; url: string }) =>
      api.post<ProjetoDocumentoLink>(`/api/v1/projetos/${projetoId}/documentos`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projetos', projetoId, 'documentos'] }),
  });
}

export function useDesligarDocumento(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => api.delete(`/api/v1/projetos/${projetoId}/documentos/${linkId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projetos', projetoId, 'documentos'] }),
  });
}

export function useProjetoIndicadores(projetoId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: QK.indicadores(projetoId ?? ''),
    queryFn: () => api.get<ProjetoIndicadores>(`/api/v1/projetos/${projetoId}/indicadores`),
    enabled: hasToken() && !!projetoId && enabled,
  });
}

/** #313: o gesto "Atualizar a venda" — recongela pela proposta de hoje; a trilha registra antes e depois. */
export function useAtualizarVendaDoProjeto(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (propostaConferidaCentavos: number) =>
      api.post<{ ok: true }>(`/api/v1/projetos/${projetoId}/venda/atualizar`, { propostaConferidaCentavos }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projetos', projetoId] }),
  });
}

export function useCreateProjeto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjetoInput) => api.post<Projeto>('/api/v1/projetos', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projetos'] }),
  });
}

export function useUpdateProjeto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjetoInput) => api.patch<Projeto>(`/api/v1/projetos/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(id) });
      qc.invalidateQueries({ queryKey: ['projetos', 'list'] });
    },
  });
}

// ─── #378 — TODA OBRA PRECISA DE UM FIM: concluir · cancelar · reabrir ─────────
function useEncerramento<TVars>(caminho: string, corpo: (v: TVars) => unknown) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: TVars & { projetoId: string }) =>
      api.post<Projeto>(`/api/v1/projetos/${v.projetoId}/${caminho}`, corpo(v)),
    onSuccess: (_d, v) => {
      // a obra inteira muda de régua (prazo, indicadores, abas que gravam): recarrega tudo de projetos
      qc.invalidateQueries({ queryKey: ['projetos'] });
      qc.invalidateQueries({ queryKey: QK.detail(v.projetoId) });
    },
  });
}
/** Concluir: grava o fim real (hoje, se não disser outra data). */
export function useConcluirProjeto() {
  return useEncerramento<{ dataFimReal?: string | null; confirmado?: boolean }>('concluir', (v) => ({
    dataFimReal: v.dataFimReal ?? null,
    ...(v.confirmado ? { confirmado: true } : {}),
  }));
}
/** Cancelar: recusado (409 `compras-vivo`) enquanto houver solicitação aberta ou classificada. */
export function useCancelarProjeto() {
  return useEncerramento<{ motivo?: string | null }>('cancelar', (v) => ({ motivo: v.motivo ?? null }));
}
/** Reabrir: quem está em Projetos — admin ou chave `projetos.ver` (403 `reabrir-e-de-projetos`; 08/10/2026). */
export function useReabrirProjeto() {
  return useEncerramento<object>('reabrir', () => ({}));
}

export function useDeleteProjeto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/projetos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projetos'] }),
  });
}

// ─── Etapas + tarefas ──────────────────────────────────────────────────

/** PRD 1.6 (mig 604): "sucessora começa depois de predecessora (+lag dias)". */
export interface Dependencia {
  id: string;
  projetoId: string;
  predecessoraId: string;
  sucessoraId: string;
  tipo: 'FS';
  lagDias: number;
  predecessoraNome?: string;
  sucessoraNome?: string;
}

/** O que o motor empurrou ao recalcular as datas — a tela DIZ, nunca silencioso. */
export interface EtapaMovida {
  id: string;
  nome: string;
  dataInicioAntes: string | null;
  dataInicioDepois: string;
  dataFimAntes: string | null;
  dataFimDepois: string;
  porCausaDe: string;
}

/** PRD 1.6 (mig 605): a linha de base — a foto do combinado, com o desvio por etapa em dias úteis. */
export interface LinhaDeBaseItem {
  etapaId: string;
  dataInicio: string | null;
  dataFim: string | null;
  dataInicioCliente: string | null;
  dataFimCliente: string | null;
  desvioFimDiasUteis: number | null;
}
export interface LinhaDeBase {
  salvoEmIso: string;
  salvoPor: string | null;
  salvoPorNome?: string;
  itens: LinhaDeBaseItem[];
}

/** CRONOGRAMA-MSP (mig 710): de onde veio o CONTRATO (= o prazo do cliente; as datas estão nas etapas). */
export type OrigemContrato = 'arquivo' | 'primeira-importacao' | 'linha-de-base-salva';
export interface ContratoDoCronograma {
  origem: OrigemContrato;
  definidoPor: string | null;
  definidoPorNome?: string;
  definidoEmIso: string;
}
/** CRONOGRAMA-MSP item 11: a última vez que o arquivo entrou (da trilha da importação). */
export interface UltimaImportacao {
  emIso: string;
  porNome: string | null;
  modo: 'importacao' | 'reimportacao';
}

export interface EtapasResposta {
  items: Etapa[];
  rollup?: ProjetoRollup;
  /** PRD 1.5: o prazo, lido do cronograma (vem junto das etapas). */
  prazo?: PrazoDoProjeto | null;
  dependencias?: Dependencia[];
  /** a ÚLTIMA VERSÃO do plano (cada reimportação a troca) */
  linhaDeBase?: LinhaDeBase | null;
  contrato?: ContratoDoCronograma | null;
  /** por etapa: fim previsto − fim do contrato, em dias úteis (positivo = atrasou) */
  desvioDoContrato?: Record<string, number | null>;
  ultimaImportacao?: UltimaImportacao | null;
}

export function useEtapas(projetoId: string | undefined) {
  return useQuery({
    queryKey: QK.etapas(projetoId ?? ''),
    queryFn: () => api.get<EtapasResposta>(`/api/v1/projetos/${projetoId}/etapas`),
    enabled: hasToken() && !!projetoId,
  });
}

/**
 * PRD 1.6 + 15/09 (A-OBRA-PRECISA-DIZER-COMO-VAI): o cronograma VEM DO MS PROJECT. Importar
 * (cronograma vazio) e REIMPORTAR (substitui, mostrando antes o que muda). Sem `confirmar` =
 * prévia, nada gravado.
 */
export interface EtapaQueMuda {
  nome: string;
  fimAntes: string | null;
  fimDepois: string | null;
  /** positivo = atrasou, negativo = adiantou; em dias úteis */
  diasUteis: number;
}
export interface EtapaQueSome {
  etapaId: string;
  nome: string;
  tarefas: number;
  custos: number;
  pedidos: number;
}
export interface MudancasReimportacao {
  atrasaram: EtapaQueMuda[];
  adiantaram: EtapaQueMuda[];
  nasceram: string[];
  sumiram: EtapaQueSome[];
  iguais: number;
  /** CRONOGRAMA-MSP: o % que muda (antes → depois), por etapa */
  avanco: AvancoQueMuda[];
  /** etapas cujo % tinha sido lançado na tela e passa a ser o do arquivo */
  avancoLancadoAqui: AvancoQueMuda[];
  /** nada muda: a confirmação é recusada pelo servidor */
  semMudanca: boolean;
  viraLinhaDeBase: boolean;
}
export interface AvancoQueMuda {
  nome: string;
  antes: number;
  depois: number;
}
export interface ContratoNaPrevia {
  situacao: 'novo-do-arquivo' | 'primeira-importacao' | 'mantido' | 'igual' | 'diferente';
  atual: ContratoDoCronograma | null;
  diferencas: Array<{ nome: string; antes: { inicio: string | null; fim: string | null }; depois: { inicio: string; fim: string } }>;
}
export interface PreviaMsProject {
  nomeProjeto: string | null;
  etapas: number;
  etapasComData: number;
  dependencias: number;
  ignoradas: Array<{ de: string; para: string; tipo: string }>;
  periodo: { inicio: string | null; fim: string | null };
  /** só quando o projeto JÁ tem cronograma: é uma reimportação */
  mudancas: MudancasReimportacao | null;
  /** CRONOGRAMA-MSP: o nome da obra aqui, e se o título do arquivo não tem nenhuma palavra em comum com ele */
  nomeObra: string | null;
  arquivoDeOutraObra: boolean;
  contrato: ContratoNaPrevia | null;
  planoDepoisDoPrometido: { prometida: string; fimPlano: string; diasUteis: number; diasCorridos: number } | null;
  perdas: { etapas: number; tarefas: number; custos: number; pedidos: number } | null;
}
export interface ResultadoImportacaoMsProject {
  modo: 'importacao' | 'reimportacao';
  etapasCriadas: number;
  etapasAtualizadas: number;
  etapasRemovidas: number;
  dependenciasCriadas: number;
  avisos: string[];
  ignoradas: Array<{ de: string; para: string; tipo: string }>;
  movidas: EtapaMovida[];
  mudancas: MudancasReimportacao | null;
  contrato: { situacao: ContratoNaPrevia['situacao']; trocado: boolean } | null;
}
export function useImportarMsProject(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { xml: string; confirmar?: boolean; trocarContrato?: boolean; aceitarPerdas?: boolean }) =>
      api.post<{ previa: PreviaMsProject; podeTrocarContrato: boolean } | ResultadoImportacaoMsProject>(
        `/api/v1/projetos/${projetoId}/cronograma/importar-msproject`,
        input,
      ),
    onSuccess: (r) => {
      if ('modo' in r) {
        qc.invalidateQueries({ queryKey: QK.etapas(projetoId) });
        qc.invalidateQueries({ queryKey: QK.indicadores(projetoId) });
        qc.invalidateQueries({ queryKey: ['projetos', 'list'] });
      }
    },
  });
}

export function useSalvarLinhaDeBase(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<LinhaDeBase>(`/api/v1/projetos/${projetoId}/cronograma/linha-de-base`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.etapas(projetoId) }),
  });
}

// 15/09 (A-OBRA-PRECISA-DIZER-COMO-VAI): os hooks de criar/apagar DEPENDÊNCIA saíram — as
// dependências vêm do arquivo do MS Project (importar/reimportar); a tela só as mostra.

/** Frase única para o aviso "N etapa(s) movida(s)" — hoje quem a usa é a (re)importação. */
export function fraseMovidas(movidas: EtapaMovida[]): string | null {
  if (!movidas.length) return null;
  const fmt = (d: string) => d.slice(0, 10).split('-').reverse().join('/');
  const lista = movidas
    .map((m) => `${m.nome} (${m.dataInicioAntes ? fmt(m.dataInicioAntes) + ' → ' : 'sem data → '}${fmt(m.dataInicioDepois)})`)
    .join(' · ');
  return `${movidas.length === 1 ? '1 etapa movida' : `${movidas.length} etapas movidas`} pelas dependências: ${lista}`;
}

// 15/09 (A-OBRA-PRECISA-DIZER-COMO-VAI): os hooks de criar/apagar ETAPA saíram — etapa nasce e
// some pela (re)importação do MS Project. O PATCH só leva o ACOMPANHAMENTO (o servidor recusa
// data, nome, ordem e árvore com 400).
export interface AcompanhamentoDaEtapa {
  status?: EtapaStatus;
  descricao?: string | null;
  responsavelId?: string | null;
  percentualConcluido?: number;
  percentualOverride?: number | null;
}
export function useUpdateEtapa(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ etapaId, patch }: { etapaId: string; patch: AcompanhamentoDaEtapa }) =>
      api.patch<Etapa & { movidas?: EtapaMovida[] }>(`/api/v1/projetos/etapas/${etapaId}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.etapas(projetoId) }),
  });
}

export function useCreateTarefa(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      etapaId,
      input,
    }: {
      etapaId: string;
      input: { titulo: string; prioridade?: TarefaPrioridade; descricao?: string | null };
    }) => api.post<Tarefa>(`/api/v1/projetos/etapas/${etapaId}/tarefas`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.etapas(projetoId) }),
  });
}

export function useUpdateTarefa(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tarefaId, patch }: { tarefaId: string; patch: Partial<Tarefa> }) =>
      api.patch<Tarefa>(`/api/v1/projetos/tarefas/${tarefaId}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.etapas(projetoId) }),
  });
}

export function useDeleteTarefa(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tarefaId: string) => api.delete<void>(`/api/v1/projetos/tarefas/${tarefaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.etapas(projetoId) }),
  });
}

// ─── Round C — lista de itens em camadas (venda/levantamento) ───────────

export type ProjetoItemCamada = 'venda' | 'levantamento';
export type ProjetoItemTipo = 'material' | 'mao_obra';

export interface ProjetoItemLinha {
  id: string;
  projetoId: string;
  camada: ProjetoItemCamada;
  tipo: ProjetoItemTipo;
  vendaItemId: string | null;
  origemOrcamentoItemId: string | null;
  descricao: string;
  codigoProduto: string | null;
  codigo: string | null;
  unidade: string | null;
  custoUnitarioCentavos: number;
  markupPct: number;
  quantidade: number;
  ordem: number;
  /** FX-39a: link/código de referência (autocomplete Omie ou manual). */
  linkReferencia: string | null;
  /** FX-39a: previsto | imprevisto. null em itens antigos. */
  classificacao: 'previsto' | 'imprevisto' | null;
  totalCustoCentavos: number;
  vendaUnitCentavos: number;
  totalVendaCentavos: number;
  lucroCentavos: number;
}

export interface ProjetoVendaResumo {
  vendaMaterialCentavos: number;
  vendaMoCentavos: number;
  vendidoTotalCentavos: number;
  custoVendaMaterialCentavos: number;
  custoVendaMoCentavos: number;
  custoVendaTotalCentavos: number;
}

export interface ProjetoLevantamentoResumo {
  temLevantamento: boolean;
  custoPrevistoMaterialCentavos: number;
  custoPrevistoMoCentavos: number;
  custoPrevistoTotalCentavos: number;
}

/** PRD 1.4 (mig 603): de onde a lista veio — null em projeto que não nasceu do Ganho. */
export interface ProjetoItensOrigem {
  cardId: string;
  cardNumero: number | null;
  orcamentoId: string | null;
  orcamentoRevisao: number | null;
  valorVendidoCentavos: number | null;
}

/**
 * 🔴 17/09/2026 — a comparação venda × levantamento, calculada no SERVIDOR (`compararLevantamento`).
 * A tela desenha; ❌ não soma nada. O PDF do levantamento usa exatamente esta conta.
 */
export type EstadoDoItemComparado = 'igual' | 'alterado' | 'novo' | 'retirado';

export interface LinhaComparada {
  /** `null` no item retirado — ele só existe na venda. */
  levantamentoItemId: string | null;
  vendaItemId: string | null;
  descricao: string;
  /** O código do PRODUTO (o que a busca devolve). */
  codigo: string | null;
  /** 🔴 #364: o número INTERNO do Omie, à parte (o item da venda tem os dois, diferentes). */
  codigoProduto: string | null;
  /** mig 633: o retirado cuja linha do levantamento está GUARDADA — restaurar devolve ELA (o mesmo id, religa ao Compras). */
  guardadoId: string | null;
  unidade: string | null;
  tipo: ProjetoItemTipo;
  estado: EstadoDoItemComparado;
  /** `null` no item NOVO (não foi orçado). */
  qtdOrcada: number | null;
  qtdLevantada: number;
  diferencaQtd: number | null;
  /** O preço unitário DA VENDA. `null` no item novo. */
  precoUnitVendaCentavos: number | null;
  totalOrcadoCentavos: number | null;
  totalLevantadoCentavos: number | null;
  semPrecoDeVenda: boolean;
  /** 18/09: foi VENDIDO com preço zero (terceiro estado). Preço e totais vêm `null`; a linha é marcada. */
  orcadoSemPreco: boolean;
  /** levantado − orçado da linha, no preço da venda. `null` quando não há preço para medir. */
  diferencaCentavos: number | null;
  imprevisto: boolean;
  semCodigo: boolean;
}

export interface MarcacaoResumo {
  itens: number;
  valorCentavos: number;
  semPreco: number;
}

export interface ResumoComparado {
  iguais: number;
  alterados: number;
  novos: number;
  retirados: number;
  orcadoTotalCentavos: number;
  levantadoTotalCentavos: number;
  diferencaCentavos: number;
  /** Quantos itens ficaram FORA da soma por não terem preço de venda. */
  foraDaSoma: number;
  /** Dos que ficaram fora: quantos não foram orçados (itens novos). */
  naoOrcados: number;
  /** Dos que ficaram fora: quantos foram orçados sem preço (vendidos a zero). */
  orcadosSemPreco: number;
  imprevistos: MarcacaoResumo;
  semCodigo: MarcacaoResumo;
}

/** 🔴 #359 (mig 633): o item NOVO removido, GUARDADO — fora das contas, restaurável. */
export interface LinhaGuardada {
  guardadoId: string;
  descricao: string;
  codigo: string | null;
  codigoProduto: string | null;
  unidade: string | null;
  tipo: ProjetoItemTipo;
  quantidade: number;
  removidoEmIso: string;
  removidoPorNome: string | null;
}

export interface LevantamentoComparado {
  linhas: LinhaComparada[];
  resumo: ResumoComparado;
  guardados?: LinhaGuardada[];
}

/** 🔴 18/09 (o buraco): o que o Compras tem de cada item do levantamento, e o aviso — pronto do servidor. */
export interface ComprasDoItem {
  projetoItemId: string;
  removido: boolean;
  descricao: string;
  codigo: string | null;
  codigoProduto: string | null;
  /** "#12 (2 un, comprado)" */
  linhas: string[];
  /** Tem linha ativa (não cancelada) no Compras. */
  noCompras: boolean;
  avisos: Array<{ tipo: 'ajustar' | 'complemento' | 'sobra' | 'retirado'; frase: string }>;
  /** 🔴 #365: todas as linhas ativas numa frase ("2 comprados · 3 em cotação"). '' sem linha ativa. */
  resumo?: string;
  /** 🔴 #365: o estado MENOS adiantado entre as linhas ativas. null sem linha ativa. */
  estado?: 'a_cotar' | 'em_cotacao' | 'comprado' | 'recebido' | 'entregue' | null;
  /** 🔴 #365: quantas linhas ATIVAS (não canceladas); o resumo só aparece com mais de uma. */
  ativas?: number;
  /** 🔴 #367: a divergência aceita pelo Compras (fato, não alarme). */
  aceita?: { frase: string; motivo: string; por: string | null; emIso: string } | null;
}

/** 🔴 #365 (decisão 2): compra que aconteceu sem custo lançado na obra. O realizado diz que está incompleto. */
export interface ComprasSemCusto {
  compras: number;
  /** 🔴 #369b: mão de obra é custo da obra, mas não é compra de fornecedor: conta à parte. */
  maoDeObra?: number;
  frase: string | null;
  /** 🔴 #369a: o caminho para lançar o custo (a cotação é a porta única). */
  caminho?: string | null;
  itens: string[];
}

/** 🔴 #365: uma linha do Compras, como o motor do elo a vê. */
export interface LinhaNoComprasDaObra {
  solicitacaoItemId: string;
  solicitacaoId: string;
  solicitacaoNumero: number | null;
  cancelada: boolean;
  quantidade: number;
  cotacaoId: string | null;
  cotacaoNumero: number | null;
  situacao: 'a_cotar' | 'em_cotacao' | 'comprado' | 'recebido' | 'entregue';
  fechada: boolean;
  custoLancado?: boolean;
}

/** 🔴 #365: o que a obra pediu, somado por item (`pedidoDaObra` no servidor). A tela só mostra. */
export interface ItemDoPedidoDaObra {
  projetoItemId: string | null;
  descricao: string;
  unidade: string | null;
  levantado: number | null;
  fora: 'removido' | 'sem-elo' | null;
  linhas: LinhaNoComprasDaObra[];
  linhasTexto: string[];
  pedido: number;
  resumo: string;
  frase: string;
  passou: number;
  avisos: Array<{ tipo: string; frase: string }>;
  semCusto: number;
}

export interface SolicitacaoDaObra {
  id: string;
  numero: number | null;
  status: string;
  cancelada: boolean;
  canceladaMotivo: string | null;
  cardDealId: string | null;
  fase: string | null;
  solicitanteNome: string | null;
  criadoEmIso: string;
  itens: number;
}

export interface PedidoDaObra {
  itens: ItemDoPedidoDaObra[];
  solicitacoes: SolicitacaoDaObra[];
  semCusto: ComprasSemCusto;
}

/** 18/09: o que foi, o que vai e o que NÃO vai ao Compras — decidido no servidor (`resumoDoEnvio`), a tela só mostra. */
export type MotivoDeNaoEnvio = 'codigo-no-link' | 'sem-produto' | 'mao-de-obra' | 'desconhecido';
export interface GrupoDoEnvio {
  itens: number;
  valorCentavos: number;
  semPreco: number;
}
export interface EnvioAoCompras {
  total: number;
  enviados: GrupoDoEnvio;
  prontos: GrupoDoEnvio;
  naoVao: Array<GrupoDoEnvio & { motivo: MotivoDeNaoEnvio; rotulo: string }>;
  /** 🔴 18/09 (decisão 2): item já comprado cujo levantamento subiu — vai como complemento no próximo envio, dito ANTES. */
  complementos?: Array<{ projetoItemId: string; quantidade: number; jaComprados: number; frase: string }>;
  frase: string;
}

export interface ProjetoItensResposta {
  venda: ProjetoItemLinha[];
  levantamento: ProjetoItemLinha[];
  /** 🔴 17/09: as duas camadas numa lista só — o que a aba mostra. */
  comparado: LevantamentoComparado;
  /** 🔴 18/09: o gerador deixou de ser mudo. */
  envio?: EnvioAoCompras;
  /**
   * 18/09: quem mexeu por último e quando (pela trilha, inclusive correção manual; `null` quando ela não tem nada).
   * `quem` null = o dado mudou depois da última linha da trilha: a tela diz que a trilha não sabe quem.
   */
  ultimaAlteracao?: { quem: string | null; quandoIso: string } | null;
  /** 🔴 18/09 (o buraco): o que o Compras tem de cada item já enviado. */
  compras?: ComprasDoItem[];
  /** 🔴 #365: compras que aconteceram sem custo lançado (o cartão "O que falta" diz que o custo real está incompleto). */
  semCusto?: ComprasSemCusto;
  resumoVenda: ProjetoVendaResumo;
  resumoLevantamento: ProjetoLevantamentoResumo;
  origem: ProjetoItensOrigem | null;
  /** 🔴 mig 640: o levantamento está congelado (quem e quando), ou `null` se está aberto. */
  congelado?: { emIso: string; porNome: string | null } | null;
  /** 🔴 mig 640: o que o aviso do congelar diz ANTES de congelar. */
  pendentesDoCompras?: { naoEnviados: number; semCodigo: number };
  /** 🔴 mig 640: quem pediu esta resposta pode REABRIR? Quem decide é o servidor. */
  podeReabrirLevantamento?: boolean;
}

/** GET /api/v1/projetos/:id/itens — camadas venda + levantamento + resumo. */
export function useProjetoItens(projetoId: string | undefined) {
  return useQuery({
    queryKey: QK.itens(projetoId ?? ''),
    queryFn: () => api.get<ProjetoItensResposta>(`/api/v1/projetos/${projetoId}/itens`),
    enabled: hasToken() && !!projetoId,
  });
}

export interface ProjetoKitInsumoSaldo {
  // OC-4: vendaCentavos = custo × (1 + markup/100); a carteira usa a venda.
  kits: Array<{ id: string; descricao: string; valorCentavos: number; markupPct: number; vendaCentavos: number }>;
  kitTotalCentavos: number;
  imprevistoConsumidoCentavos: number;
  saldoCentavos: number;
  estourado: boolean;
}

/** GET /api/v1/projetos/:id/kit-insumo — saldo da carteira de imprevistos (C5). */
export function useProjetoKitInsumo(projetoId: string | undefined) {
  return useQuery({
    queryKey: ['projetos', projetoId ?? '', 'kit-insumo'] as const,
    queryFn: () => api.get<ProjetoKitInsumoSaldo>(`/api/v1/projetos/${projetoId}/kit-insumo`),
    enabled: hasToken() && !!projetoId,
  });
}

// ─── Camada VENDA e LEVANTAMENTO (Round C / C2) — o aditivo velho (C6) morreu na 1.3 do PRD ──

/** POST importar a camada VENDA do orçamento vinculado (idempotente). */
export function useImportarOrcamento(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ status: string; importados: number }>(
        `/api/v1/projetos/${projetoId}/itens/importar-orcamento`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
      qc.invalidateQueries({ queryKey: QK.indicadores(projetoId) }); // o motor da obra (vendido/custo/margem)
    },
  });
}

// C2 — levantamento (previsto editável)
export function useIniciarLevantamento(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ status: string }>(`/api/v1/projetos/${projetoId}/itens/levantamento/iniciar`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
      qc.invalidateQueries({ queryKey: QK.indicadores(projetoId) }); // o motor da obra (vendido/custo/margem)
    },
  });
}

export function useAddLevantamentoItem(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    // FX-39a: custo é OPCIONAL (o projeto não digita custo); +link +classificação.
    mutationFn: (input: {
      tipo?: ProjetoItemTipo;
      descricao: string;
      unidade?: string | null;
      custoUnitarioCentavos?: number;
      quantidade: number;
      linkReferencia?: string | null;
      /** 18/09: obrigatória — nada nasce sozinho. */
      classificacao: 'previsto' | 'imprevisto';
      /** Fase 2 de Compras: o código do catálogo (do autocomplete). Sem código, o item NÃO vai ao Compras. */
      codigo?: string | null;
    }) => api.post<ProjetoItensResposta>(`/api/v1/projetos/${projetoId}/itens/levantamento`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
      qc.invalidateQueries({ queryKey: QK.indicadores(projetoId) }); // o motor da obra (vendido/custo/margem)
    },
  });
}

/**
 * 🔴 mig 640 — CONGELAR o levantamento. Sem `confirmado`, a rota RECUSA com 409 `pendentes-no-compras` e a frase
 * (*"30 itens não foram ao Compras (28 sem código). Congelar assim mesmo?"*): a tela pergunta e reenvia com `confirmado: true`.
 * ❌ Nunca congela sozinho.
 */
export function useCongelarLevantamento(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { confirmado?: true } = {}) =>
      api.post<ProjetoItensResposta>(`/api/v1/projetos/${projetoId}/itens/levantamento/congelar`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
    },
  });
}

/** 🔴 mig 640 — REABRIR. Só admin (a rota devolve 403 para o resto, com o caminho). */
export function useReabrirLevantamento(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ProjetoItensResposta>(`/api/v1/projetos/${projetoId}/itens/levantamento/reabrir`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
    },
  });
}

/** FX-39a: gera a solicitação de compra com os itens do levantamento não enviados. */
export function useGerarSolicitacaoLevantamento(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ status: 'ok' | 'nada-novo'; itensEnviados: number; itensSemCodigo?: number; complementos?: number; solicitacaoId?: string; envio?: EnvioAoCompras }>(
        `/api/v1/projetos/${projetoId}/itens/levantamento/gerar-solicitacao`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
      qc.invalidateQueries({ queryKey: ['projetos', projetoId, 'compras-info'] });
      // o card-espelho novo aparece no Quadro de Compras.
      qc.invalidateQueries({ queryKey: ['v1', 'compras', 'quadro'] });
    },
  });
}

// ─── FX-39b — retorno das infos do Compras por item do levantamento ───────

// ─── FX-39b — retorno das infos do Compras por item do levantamento ───────
export type LevantamentoCompraStatus =
  | 'a_cotar'
  | 'em_cotacao'
  | 'aprovado'
  | 'comprado'
  | 'recebido'
  | 'entregue';

export interface LevantamentoCompraInfo {
  projetoItemId: string;
  status: LevantamentoCompraStatus;
  /** O custo REAL na obra (desde a Fase 2 = o recebido quando a nota chegou). */
  custoRealCentavos: number | null;
  /** Fase 2: fechado × recebido — a obra mostra os dois quando divergem. */
  custoFechadoCentavos: number | null;
  custoRecebidoCentavos: number | null;
  prazo: string | null;
}

/** GET /api/v1/projetos/:id/itens/compras-info — status/custo real/prazo por item. */
export function useLevantamentoComprasInfo(projetoId: string | undefined) {
  return useQuery({
    queryKey: ['projetos', projetoId ?? '', 'compras-info'] as const,
    queryFn: () =>
      api.get<{ items: LevantamentoCompraInfo[] }>(`/api/v1/projetos/${projetoId}/itens/compras-info`),
    enabled: hasToken() && !!projetoId,
  });
}

// ─── FX-40 — margem real do projeto (venda − custo real do Compras) ───────
export interface ProjetoMargem {
  projetoId: string;
  nome: string;
  valorVendaTotal: number | null; // REAIS
  totalRealizado: number; // REAIS (SUM projeto_custos realizado)
  margemAbsoluta: number | null; // REAIS
  margemPercentual: number | null;
}

/** GET /api/v1/projetos/:id/margem — margem REAL (venda − custo real). Valores em REAIS. */
export function useProjetoMargem(projetoId: string | undefined) {
  return useQuery({
    queryKey: ['projetos', projetoId ?? '', 'margem'] as const,
    queryFn: () => api.get<ProjetoMargem>(`/api/v1/projetos/${projetoId}/margem`),
    enabled: hasToken() && !!projetoId,
  });
}

export function useUpdateProjetoItem(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      patch,
    }: {
      itemId: string;
      patch: { descricao?: string; custoUnitarioCentavos?: number; quantidade?: number };
    }) => api.patch<ProjetoItensResposta>(`/api/v1/projetos/${projetoId}/itens/${itemId}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
      qc.invalidateQueries({ queryKey: QK.indicadores(projetoId) }); // o motor da obra (vendido/custo/margem)
    },
  });
}

/**
 * 🔴 18/09 (Frente 8 + mig 633): restaura o item RETIRADO (pela linha da venda) ou o item novo GUARDADO (#359). Se a linha do
 * levantamento está guardada, volta ELA (o mesmo id) e religa ao Compras. A resposta traz a `frase` pronta (e a divergência, se há).
 */
export function useRestaurarRetirado(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alvo: { vendaItemId: string } | { guardadoId: string }) =>
      api.post<ProjetoItensResposta & { frase?: string }>(`/api/v1/projetos/${projetoId}/itens/levantamento/restaurar`, alvo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
      qc.invalidateQueries({ queryKey: QK.indicadores(projetoId) });
      qc.invalidateQueries({ queryKey: ['projetos', projetoId, 'compras-info'] });
    },
  });
}

/**
 * 🔴 18/09 (decisão 4): remover item já enviado ao Compras pede CONFIRMAÇÃO (`confirmado`); comprado é recusado pelo servidor
 * (409 `comprado-no-compras`, com a frase). O item removido é guardado e pode voltar.
 */
export function useDeleteProjetoItem(projetoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alvo: { itemId: string; confirmado?: boolean }) =>
      api.delete<ProjetoItensResposta>(`/api/v1/projetos/${projetoId}/itens/${alvo.itemId}${alvo.confirmado ? '?confirmado=1' : ''}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.itens(projetoId) });
      qc.invalidateQueries({ queryKey: QK.indicadores(projetoId) }); // o motor da obra (vendido/custo/margem)
      qc.invalidateQueries({ queryKey: ['projetos', projetoId, 'compras-info'] });
    },
  });
}

// ─── Solicitações (F2.2) ──────────────────────────────────────────────────

export function useSolicitacoes(projetoId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: QK.solicitacoes(projetoId ?? ''),
    queryFn: () =>
      api.get<{ items: SolicitacaoProjeto[] }>(`/api/v1/projetos/${projetoId}/solicitacoes`),
    enabled: hasToken() && !!projetoId && enabled,
  });
}

/** 🔴 #365: a aba "Compras da obra" — as solicitações da obra, somadas por item. */
export function useComprasDaObra(projetoId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: QK.comprasDaObra(projetoId ?? ''),
    queryFn: () => api.get<PedidoDaObra>(`/api/v1/projetos/${projetoId}/compras-da-obra`),
    enabled: hasToken() && !!projetoId && enabled,
  });
}

// ─── Tipos ──────────────────────────────────────────────────────────────

export function useProjetoTipos(ativo?: boolean) {
  return useQuery({
    queryKey: [...QK.tipos(), ativo],
    queryFn: () =>
      api.get<{ items: ProjetoTipo[] }>(
        `/api/v1/projeto-tipos${ativo === undefined ? '' : `?ativo=${ativo}`}`,
      ),
    enabled: hasToken(),
  });
}

export function useProjetoTipo(id: string | undefined) {
  return useQuery({
    queryKey: QK.tipoDetail(id ?? ''),
    queryFn: () => api.get<ProjetoTipo>(`/api/v1/projeto-tipos/${id}`),
    enabled: hasToken() && !!id,
  });
}

export function useCreateProjetoTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      nome: string;
      descricao?: string | null;
      workflowJson?: string | null;
      parentTipoId?: string | null;
    }) => api.post<ProjetoTipo>('/api/v1/projeto-tipos', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projeto-tipos'] }),
  });
}

export function useUpdateProjetoTipo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      nome?: string;
      descricao?: string | null;
      ativo?: boolean;
      parentTipoId?: string | null;
    }) => api.patch<ProjetoTipo>(`/api/v1/projeto-tipos/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projeto-tipos'] }),
  });
}

export function useDeleteProjetoTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/projeto-tipos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projeto-tipos'] }),
  });
}

export function useCreateWorkflowStep(tipoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ordem: number;
      nome: string;
      descricao?: string | null;
      requerAprovacao?: boolean;
      perfilAprovador?: string | null;
    }) => api.post<WorkflowStep>(`/api/v1/projeto-tipos/${tipoId}/workflow-steps`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.tipoDetail(tipoId) }),
  });
}

export function useDeleteWorkflowStep(tipoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stepId: string) =>
      api.delete<void>(`/api/v1/projeto-tipos/workflow-steps/${stepId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.tipoDetail(tipoId) }),
  });
}
